import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { DialogEyebrow } from './DialogPrimitives';
import { FormErrorBlock } from './FormErrorBlock';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  /** Tiny eyebrow above the title — context like "Edit · BX-CORR-L". */
  eyebrow?: React.ReactNode;
  /** Right-aligned header content — secondary actions, status badges, etc. */
  headerExtra?: React.ReactNode;
  /** Replaces the entire header (title/description/eyebrow/extra). Use when a form needs a custom hero. */
  headerSlot?: React.ReactNode;
  submitLabel?: string;
  /** Optional contextual loading label (e.g. "Creating plan…"). Auto-derives from submitLabel when absent. */
  submittingLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  error?: unknown;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
  /** Slot for custom footer actions (e.g. destructive remove on edit). */
  extraFooter?: React.ReactNode;
  /** Hide built-in submit/cancel — useful when the form is in a confirmation state. */
  hideFooter?: boolean;
  className?: string;
  /** Centered popup max-width. Defaults to `3xl` (768px) — the "maximum width" pattern. */
  size?: 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  /**
   * Lock the dialog to a fixed height so its size doesn't reflow as the body
   * content varies (multi-step wizards, tabs). Internal scroll absorbs the
   * variation. Default `false` — small one-screen forms hug their content.
   *
   * `'lg'` = 80vh, `'xl'` = 85vh, `true` = 80vh shorthand.
   */
  fixedHeight?: boolean | 'lg' | 'xl';
  /**
   * Pinned tab strip — when provided, FormSheet wraps the body in `<Tabs>`
   * and renders the triggers above the scroll area (so the tabs stay put
   * while the form content scrolls). Children should be `<TabsContent>`
   * elements, mirroring the InventoryDetailSheet pattern. Use the shared
   * `DIALOG_TAB_TRIGGER` className on each trigger.
   */
  tabs?: {
    value: string;
    onValueChange: (v: string) => void;
    triggers: React.ReactNode;
  };
}

const SIZE_CLASS: Record<NonNullable<Props['size']>, string> = {
  md:  'max-w-md',
  lg:  'max-w-lg',
  xl:  'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

/**
 * Centered max-width popup that hosts an admin form. Switched from a
 * right-side slide-in sheet to a centered dialog for better focus and to
 * accommodate dense / multi-column forms. Everything inside the body
 * scrolls; header and footer stay pinned. Forms inside should render
 * `FormField`s + inputs as plain children.
 *
 * Cascading polish (no per-form change needed):
 *  - Backdrop dim + blur (Dialog primitive)
 *  - First interactive field auto-focuses on open
 *  - ⌘↵ submits from any focused field
 *  - Submit-label auto-conjugates to a loading state ("Creating plan…")
 *  - Footer shows keyboard hints (esc · ⌘↵)
 */
export function FormSheet({
  open,
  onOpenChange,
  title,
  description,
  eyebrow,
  headerExtra,
  headerSlot,
  submitLabel = 'Save',
  submittingLabel,
  cancelLabel = 'Cancel',
  isSubmitting,
  error,
  onSubmit,
  children,
  extraFooter,
  hideFooter,
  className,
  size = '3xl',
  fixedHeight,
  tabs,
}: Props) {
  const loadingLabel = submittingLabel ?? deriveLoadingLabel(submitLabel);
  const heightClass =
    fixedHeight === 'xl'
      ? 'h-[85vh]'
      : fixedHeight === 'lg' || fixedHeight === true
        ? 'h-[80vh]'
        : 'max-h-[88vh]';

  // Shared error block — extracted into FormErrorBlock so wizard/
  // multi-step flows can render the same per-field validation detail
  // without reimplementing the parse. See feedback_validation_field_details.md.
  const errorBlock = error ? (
    <FormErrorBlock error={error} className="mx-7 mb-5" />
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex w-full flex-col gap-0 overflow-hidden p-0',
          heightClass,
          SIZE_CLASS[size],
          className,
        )}
        onOpenAutoFocus={(e) => {
          // Override Radix default (focuses the close button) — focus the
          // first interactive form field instead. Skips disabled fields and
          // [data-no-autofocus] opt-outs.
          const root = e.currentTarget as HTMLElement;
          const first = root.querySelector<HTMLElement>(
            'input:not([type="submit"]):not([type="button"]):not([disabled]):not([data-no-autofocus]), textarea:not([disabled]), [role="combobox"]:not([disabled])',
          );
          if (first) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <DialogHeader className="px-7 pt-6 pb-5">
          {headerSlot ?? (
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1 space-y-1.5">
                {eyebrow && <DialogEyebrow>{eyebrow}</DialogEyebrow>}
                <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
                  {title}
                </DialogTitle>
                {description && (
                  <DialogDescription className="text-[12.5px] leading-relaxed">
                    {description}
                  </DialogDescription>
                )}
              </div>
              {headerExtra && (
                <div className="flex shrink-0 items-center gap-1.5">{headerExtra}</div>
              )}
            </div>
          )}
        </DialogHeader>
        <Separator />
        <form
          onSubmit={onSubmit}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting) {
              e.preventDefault();
              e.currentTarget.requestSubmit();
            }
          }}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          {tabs ? (
            <Tabs
              value={tabs.value}
              onValueChange={tabs.onValueChange}
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
            >
              <div className="overflow-x-auto border-b border-border/60 bg-background px-7">
                <TabsList className="h-10 w-max flex-nowrap gap-2 bg-transparent p-0">
                  {tabs.triggers}
                </TabsList>
              </div>
              <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-4 px-7 py-5">{children}</div>
                {errorBlock}
              </ScrollArea>
            </Tabs>
          ) : (
            <ScrollArea className="min-h-0 flex-1">
              <div className="space-y-4 px-7 py-5">{children}</div>
              {errorBlock}
            </ScrollArea>
          )}
          {!hideFooter && (
            <div className="flex items-center gap-2 border-t border-border/60 bg-muted/30 px-4 py-3">
              <div className="hidden items-center gap-3 text-[10.5px] text-muted-foreground sm:flex">
                <span className="inline-flex items-center gap-1">
                  <Badge variant="muted" className="font-mono text-[9px]">esc</Badge>
                  cancel
                </span>
                <span className="inline-flex items-center gap-1">
                  <Badge variant="muted" className="font-mono text-[9px]">⌘ ↵</Badge>
                  submit
                </span>
              </div>
              {extraFooter}
              <div className="ml-auto flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  {cancelLabel}
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? loadingLabel : submitLabel}
                </Button>
              </div>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

const VERB_TO_ING: Record<string, string> = {
  save: 'Saving',
  create: 'Creating',
  generate: 'Generating',
  invite: 'Inviting',
  request: 'Requesting',
  update: 'Updating',
  add: 'Adding',
  register: 'Registering',
  remove: 'Removing',
  delete: 'Deleting',
  archive: 'Archiving',
  confirm: 'Confirming',
  send: 'Sending',
  publish: 'Publishing',
  rotate: 'Rotating',
  revoke: 'Revoking',
  enroll: 'Enrolling',
  apply: 'Applying',
};

// Turn a DTO field name like "categoryId" or "parent_id" into a label
// an operator can read at a glance: "Category Id" / "Parent Id".
function deriveLoadingLabel(submitLabel: string): string {
  const re = new RegExp(`(${Object.keys(VERB_TO_ING).join('|')})\\b`, 'i');
  const match = submitLabel.match(re);
  if (!match) return 'Submitting…';
  const ing = VERB_TO_ING[match[1].toLowerCase()];
  const replaced = submitLabel.replace(match[0], match.index === 0 ? ing : ing.toLowerCase());
  return `${replaced}…`;
}
